/** packages/shared-components/src/hooks/useToast.tsx */

import { useCallback } from "react";
import { createRoot, type Root } from "react-dom/client";
import { getSubAppPortalContainer } from "@eweadn-qiankun/shared-utils";
import { Toast, type ToastType } from "../components/Toast";

export interface ToastOptions {
  title?: string;
  message: string;
  type?: ToastType;
  duration?: number;
  /** 默认 toast.svg */
  iconSrc?: string;
  actionText?: string;
  onAction?: () => void;
  /** 关闭时回调（点关闭、点操作按钮或自动消失） */
  onDismiss?: () => void;
  /** 相同 key 仅保留一条，再次弹出时更新内容 */
  dedupeKey?: string;
}

type ToastEntry = {
  id: number;
  options: ToastOptions;
  root: Root;
  node: HTMLDivElement;
};

let hostEl: HTMLDivElement | null = null;
let seq = 0;
const entries: ToastEntry[] = [];

function ensureHost() {
  if (hostEl && document.body.contains(hostEl)) return hostEl;
  hostEl = document.createElement("div");
  hostEl.className = "shared-toast-host";
  getSubAppPortalContainer().appendChild(hostEl);
  return hostEl;
}

function restack() {
  entries.forEach((entry) => {
    entry.root.render(
      <Toast
        title={entry.options.title}
        message={entry.options.message}
        type={entry.options.type || "info"}
        duration={entry.options.duration ?? 5000}
        iconSrc={entry.options.iconSrc}
        actionText={entry.options.actionText}
        onAction={entry.options.onAction}
        onDismiss={entry.options.onDismiss}
        onClose={() => dismissToast(entry.id)}
      />
    );
  });
}

function dismissToast(id: number) {
  const idx = entries.findIndex((e) => e.id === id);
  if (idx < 0) return;
  const [entry] = entries.splice(idx, 1);
  entry.root.unmount();
  entry.node.remove();
  restack();
  if (entries.length === 0 && hostEl) {
    hostEl.remove();
    hostEl = null;
  }
}

/** 命令式弹出 Toast（勋章通知 / 系统公告等） */
export function showToast(options: ToastOptions) {
  const dedupeKey = options.dedupeKey?.trim();
  if (dedupeKey) {
    const existing = entries.find((e) => e.options.dedupeKey === dedupeKey);
    if (existing) {
      existing.options = { ...existing.options, ...options, dedupeKey };
      restack();
      return existing.id;
    }
  }

  const host = ensureHost();
  const id = ++seq;
  const node = document.createElement("div");
  node.className = "shared-toast-slot";
  host.appendChild(node);
  const root = createRoot(node);
  const entry: ToastEntry = { id, options, root, node };
  entries.push(entry);
  restack();
  return id;
}

export const useToast = () => {
  const showToastCallback = useCallback((options: ToastOptions) => {
    showToast(options);
  }, []);

  const success = useCallback(
    (message: string, duration?: number) => {
      showToastCallback({ message, type: "success", duration });
    },
    [showToastCallback]
  );

  const error = useCallback(
    (message: string, duration?: number) => {
      showToastCallback({ message, type: "error", duration });
    },
    [showToastCallback]
  );

  const warning = useCallback(
    (message: string, duration?: number) => {
      showToastCallback({ message, type: "warning", duration });
    },
    [showToastCallback]
  );

  const info = useCallback(
    (message: string, duration?: number) => {
      showToastCallback({ message, type: "info", duration });
    },
    [showToastCallback]
  );

  /** 标题 + 正文 + 操作按钮（系统公告 / 勋章通知等） */
  const notify = useCallback(
    (options: ToastOptions) => {
      showToastCallback({ type: "info", duration: 0, ...options });
    },
    [showToastCallback]
  );

  return {
    showToast: showToastCallback,
    notify,
    success,
    error,
    warning,
    info,
  };
};
